import { useRef } from 'react'

import { MARKDOWN_REGULAR_BOLD_CLASS } from '@/components/ui/MarkdownBody'
import { cn } from '@/lib/utils'

/**
 * A `<textarea>` that visually dims `##`/`**` markers and bolds the text
 * they wrap, live, while staying a real textarea underneath.
 *
 * TWO LAYERS, ONE SOURCE OF TRUTH. The textarea is the actual input — same
 * `value`/`onChange`/`data-testid` a plain `Textarea` would have, so every
 * existing `fireEvent.change` test against this element keeps working. Its
 * own text is painted transparent (only the caret and selection stay
 * visible); an `aria-hidden` layer behind it renders the highlighted
 * version, kept pixel-aligned (identical padding/border width/font/wrapping)
 * and scroll-synced by hand, since two independently-scrolling boxes drift
 * apart the moment the body is long enough to scroll.
 *
 * Not a markdown parser: it only recognizes the two patterns gate artifact
 * bodies actually use (an ATX heading line, a `**bold**` span). Anything
 * else passes through unstyled — correct, since the textarea underneath is
 * still the raw, unaltered source of truth either way.
 */

interface HighlightToken {
  text: string
  variant: 'plain' | 'marker' | 'bold'
}

const HEADING_LINE_RE = /^(#{1,4})(\s+)(.*)$/
const BOLD_SPAN_RE = /\*\*(.+?)\*\*/g

export function tokenizeMarkdownLite(content: string): Array<HighlightToken> {
  const lines = content.split('\n')
  const tokens: Array<HighlightToken> = []

  lines.forEach((line, index) => {
    const heading = line.match(HEADING_LINE_RE)
    if (heading) {
      const [, hashes, gap, rest] = heading
      tokens.push({ text: hashes + gap, variant: 'marker' })
      tokens.push(...tokenizeBoldSpans(rest, 'bold'))
    } else {
      tokens.push(...tokenizeBoldSpans(line, 'plain'))
    }
    if (index < lines.length - 1) {
      tokens.push({ text: '\n', variant: 'plain' })
    }
  })

  return tokens
}

/**
 * `baseVariant` lets a heading's own text stay bold even outside a `**`
 * span of its own — the whole line is already bold, `**` inside it just
 * adds a dimmed marker on top rather than a second bold treatment.
 */
function tokenizeBoldSpans(
  text: string,
  baseVariant: 'plain' | 'bold',
): Array<HighlightToken> {
  const tokens: Array<HighlightToken> = []
  let lastIndex = 0

  for (const match of text.matchAll(BOLD_SPAN_RE)) {
    const start = match.index ?? 0
    if (start > lastIndex) {
      tokens.push({ text: text.slice(lastIndex, start), variant: baseVariant })
    }
    tokens.push({ text: '**', variant: 'marker' })
    tokens.push({ text: match[1], variant: 'bold' })
    tokens.push({ text: '**', variant: 'marker' })
    lastIndex = start + match[0].length
  }

  if (lastIndex < text.length || tokens.length === 0) {
    tokens.push({ text: text.slice(lastIndex), variant: baseVariant })
  }

  return tokens
}

function tokenClassName(variant: HighlightToken['variant']): string | undefined {
  if (variant === 'marker') {
    return 'text-muted-foreground/50'
  }
  if (variant === 'bold') {
    return MARKDOWN_REGULAR_BOLD_CLASS
  }
  return undefined
}

export interface MarkdownHighlightRange {
  value: string
  start: number
  end: number
}

/**
 * Wrap or unwrap `**` around the current selection. Selection already wrapped,
 * or sitting inside a wrap, unwraps. Empty selection inserts `****` and parks
 * the caret in the middle.
 */
export function toggleMarkdownBold(
  value: string,
  start: number,
  end: number,
): MarkdownHighlightRange {
  const from = Math.max(0, Math.min(start, end, value.length))
  const to = Math.max(0, Math.min(Math.max(start, end), value.length))
  const selected = value.slice(from, to)

  if (selected.startsWith('**') && selected.endsWith('**') && selected.length >= 4) {
    const inner = selected.slice(2, -2)
    return {
      value: value.slice(0, from) + inner + value.slice(to),
      start: from,
      end: from + inner.length,
    }
  }

  if (
    from >= 2 &&
    to + 2 <= value.length &&
    value.slice(from - 2, from) === '**' &&
    value.slice(to, to + 2) === '**'
  ) {
    return {
      value: value.slice(0, from - 2) + selected + value.slice(to + 2),
      start: from - 2,
      end: to - 2,
    }
  }

  const wrapped = `**${selected}**`
  return {
    value: value.slice(0, from) + wrapped + value.slice(to),
    start: from + 2,
    end: to + 2,
  }
}

export interface MarkdownHighlightTextareaProps {
  value: string
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  rows?: number
  className?: string
  disabled?: boolean
  'aria-label': string
  'data-testid'?: string
}

// Border WIDTH must match between layers so their padding boxes align
// pixel-for-pixel; only the color differs (transparent vs. visible).
const SHARED_BOX_CLASSES =
  'whitespace-pre-wrap break-words rounded-md border px-3 py-2 text-xs leading-relaxed'

export function MarkdownHighlightTextarea(props: MarkdownHighlightTextareaProps) {
  const {
    value,
    onChange,
    rows,
    className,
    disabled,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
  } = props

  const overlayRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function syncScroll(event: React.UIEvent<HTMLTextAreaElement>) {
    const overlay = overlayRef.current
    if (!overlay) {
      return
    }
    overlay.scrollTop = event.currentTarget.scrollTop
    overlay.scrollLeft = event.currentTarget.scrollLeft
  }

  function handleBoldShortcut(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (disabled || event.key.toLowerCase() !== 'b' || !(event.metaKey || event.ctrlKey)) {
      return
    }
    event.preventDefault()
    const textarea = event.currentTarget
    const next = toggleMarkdownBold(value, textarea.selectionStart, textarea.selectionEnd)
    onChange({
      target: { value: next.value },
    } as React.ChangeEvent<HTMLTextAreaElement>)
    requestAnimationFrame(() => {
      const node = textareaRef.current
      if (!node) {
        return
      }
      node.setSelectionRange(next.start, next.end)
    })
  }

  const tokens = tokenizeMarkdownLite(value)

  return (
    <div className="relative">
      <div
        ref={overlayRef}
        aria-hidden="true"
        className={cn(
          SHARED_BOX_CLASSES,
          'pointer-events-none absolute inset-0 overflow-hidden border-transparent bg-background text-foreground',
          disabled && 'opacity-50',
          className,
        )}
      >
        {tokens.map((token, index) => (
          <span key={index} className={tokenClassName(token.variant)}>
            {token.text}
          </span>
        ))}
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onKeyDown={handleBoldShortcut}
        onScroll={syncScroll}
        rows={rows}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        className={cn(
          SHARED_BOX_CLASSES,
          'relative w-full resize-y border-input bg-transparent text-transparent caret-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      />
    </div>
  )
}
